import {
  type ClampableMarginField,
  createMarginClamp,
  type MarginClamp,
  type MarginClamps,
  readMarginClamps,
} from "@project/shared";
import {
  clampMarginToRange,
  type MarginAxisRange,
} from "./addableMarginDragRange";

// The record's reads need no geometry and live in `@project/shared`, where
// the preset paths of the backend reach them too.
export {
  activeMarginClamps,
  CLAMPABLE_MARGIN_FIELDS,
  type ClampableMarginField,
  marginAlignment,
  readMarginClamps,
  withoutMarginClamp,
  withRequestedMargins,
} from "@project/shared";

/** A margin after one build: the value to store and the clamp behind it. */
export interface SettledMargin {
  value: number;
  clamp: MarginClamp | undefined;
}

/**
 * Where a margin settles in `range`, remembering what was asked for when that
 * does not fit.
 *
 * A value still sitting at its recorded `applied` has not been touched since
 * the clamp, so the original request stands and is tried again — which is how
 * a margin returns once the case has room for it. A value that moved off
 * `applied` was set on purpose and replaces the old request. So does a change
 * of `alignment`, the anchor the margin is measured from: the old request
 * names a position from another edge, and restoring it would move the part.
 */
export function settleMargin(
  value: number,
  clamp: MarginClamp | undefined,
  range: MarginAxisRange,
  alignment?: string,
): SettledMargin {
  const requested =
    clamp?.applied === value && clamp.alignment === alignment
      ? clamp.requested
      : value;
  const applied = clampMarginToRange(requested, range);
  return {
    value: applied,
    clamp:
      applied === requested
        ? undefined
        : createMarginClamp(requested, applied, alignment),
  };
}

function sameClamp(a: MarginClamp | undefined, b: MarginClamp | undefined) {
  return (
    a?.requested === b?.requested &&
    a?.applied === b?.applied &&
    a?.alignment === b?.alignment
  );
}

/**
 * `option` with the settled margins written in. Returns the option itself when
 * nothing moved, so callers can detect "unchanged" by identity. Clamps of
 * fields not in `settled` are kept: no range this build is no reason to forget
 * the request.
 */
export function applySettledMargins<T extends { marginClamps?: MarginClamps }>(
  option: T,
  settled: Partial<Record<ClampableMarginField, SettledMargin>>,
): T {
  const current = option as T & Partial<Record<ClampableMarginField, unknown>>;
  const clamps = readMarginClamps(option);
  const values: Partial<Record<ClampableMarginField, number>> = {};
  let changed = false;

  for (const [field, margin] of Object.entries(settled) as [
    ClampableMarginField,
    SettledMargin,
  ][]) {
    values[field] = margin.value;
    if (
      current[field] !== margin.value ||
      !sameClamp(clamps[field], margin.clamp)
    ) {
      changed = true;
    }
    if (margin.clamp) {
      clamps[field] = margin.clamp;
    } else {
      delete clamps[field];
    }
  }

  if (!changed) {
    return option;
  }
  // No clamps left is an absent key, not `marginClamps: {}` or `undefined`, so
  // a restored option compares equal to one that was never clamped.
  const { marginClamps: _previous, ...rest } = option;
  return {
    ...rest,
    ...values,
    ...(Object.keys(clamps).length > 0 ? { marginClamps: clamps } : {}),
  } as T;
}
